// Login — pantalla bienvenida. Si el dispositivo tiene PIN configurado,
// muestra directamente la pantalla de PIN. Si tras verificar el PIN
// no hay sesión backend válida, fuerza Google login automáticamente.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../auth/AuthContext";
import { hasPin, removePin } from "../auth/pinStore";
import PinLock from "./PinLock";
import InstallBanner from "../components/InstallBanner";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [forceGoogle, setForceGoogle] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);
  const pinConfigured = hasPin();

  useEffect(() => {
    if (user && !loading) navigate("/viajes", { replace: true });
  }, [user, loading, navigate]);

  const onPinSuccess = async (info) => {
    // info.authed === true means PinLock already obtained a valid session via /pin/login
    if (info && info.authed) {
      navigate("/viajes", { replace: true });
      return;
    }
    // Legacy path: PIN verified locally but no server session yet. Try /me.
    setCheckingSession(true);
    try {
      await api("/api/auth/me");
      navigate("/viajes", { replace: true });
    } catch {
      // No hay sesión válida → forzar Google login (una sola vez para enrolar el device)
      login();
    }
  };

  const onForgotPin = () => {
    if (window.confirm("¿Olvidaste tu PIN? Vas a entrar con Google y podrás crear uno nuevo.")) {
      removePin();
      setForceGoogle(true);
    }
  };

  // Si hay PIN configurado, mostrar PIN screen directo
  if (pinConfigured && !forceGoogle) {
    return (
      <div data-testid="login-pin-mode">
        <PinLock
          mode="verify"
          hint={checkingSession ? "Verificando sesión..." : "Bienvenido de vuelta"}
          onSuccess={onPinSuccess}
        />
        {/* Banner de instalación PWA y opciones de escape */}
        <div className="fixed bottom-4 left-0 right-0 px-6 z-50">
          <div className="max-w-sm mx-auto">
            <InstallBanner />
            <div className="flex flex-col items-center gap-1.5">
              <button onClick={() => setForceGoogle(true)} className="text-xs text-[hsl(var(--accent))] underline" data-testid="login-use-google">
                Usar Google en su lugar
              </button>
              <button onClick={onForgotPin} className="text-[10px] text-[hsl(var(--muted))]" data-testid="login-forgot-pin">
                Olvidé mi PIN
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-10"
      style={{ background: "linear-gradient(180deg, hsl(35 38% 96%) 0%, hsl(35 30% 92%) 100%)" }}
      data-testid="login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-sm w-full"
      >
        <div className="text-center mb-10">
          <img src="/icon-192.png" alt="Bitácora" className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-md" />
          <h1 className="text-4xl font-bold tracking-tight leading-tight" style={{ fontFamily: '"Fraunces", "Liberation Serif", serif' }}>Bitácora</h1>
          <p className="text-sm text-[hsl(var(--muted))] mt-2 leading-relaxed">
            Tu diario de viaje.
            <br />
            Todos tus rumbos en un solo lugar.
          </p>
        </div>

        <button
          onClick={login}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-2xl bg-white shadow-sm border border-[hsl(var(--line))] hover:shadow-md active:scale-[0.98] transition-all"
          data-testid="login-google"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          <span className="text-sm font-semibold">Continuar con Google</span>
        </button>

        {pinConfigured && (
          <button onClick={() => setForceGoogle(false)} className="block mx-auto mt-4 text-xs text-[hsl(var(--muted))] underline" data-testid="login-back-pin">
            ← Volver al PIN
          </button>
        )}

        <p className="text-[11px] text-center text-[hsl(var(--muted))] mt-6 leading-relaxed">
          Tras el primer login con Google podrás
          <br />
          configurar un <strong>PIN de 4 dígitos</strong> para entrar más rápido.
        </p>
      </motion.div>
    </div>
  );
}
